import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Transition as _Transition, withCtx as _withCtx, withModifiers as _withModifiers, resolveComponent as _resolveComponent, createSlots as _createSlots, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mt-x2" }
const _hoisted_2 = { class: "ml-10" }
const _hoisted_3 = {
  key: 0,
  class: "mx-auto max-w-600"
}
const _hoisted_4 = { key: 2 }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["disabled", "onClick"]
const _hoisted_7 = { key: 1 }
const _hoisted_8 = {
  key: 2,
  class: "flex items-center justify-between"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "c-magic-line" }
const _hoisted_11 = ["onClick"]

import {
    BaseProductItem,
    ClickAndCollectStoreInventoryResult, BasketDeliverableForClickAndCollect,
    BasketDeliverableForPickAndPack,
    CheckoutDeliverableType,
} from '@/b2c/shared/api/server-apis';
import { StoreModel } from '@/types/locationServerApi';
import BlockLabelWithNamedArguments from '@/shared/BlockLabelWithNamedArguments.vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import { computed, ComputedRef, Ref, ref, unref } from 'vue';
import BasketDeliverableDialog from '@/b2c/basket/ui/BasketDeliverableDialog.vue';
import { DeliverableSelectItem, useDeliverables } from '@/b2c/basket/useDeliverables';
import BasketDeliverableItemsSelection from '@/b2c/basket/ui/BasketDeliverableItemsSelection.vue';
import BasketDeliverableStoreSelection from '@/b2c/basket/ui/BasketDeliverableStoreSelection.vue';
import useBasket from '@/b2c/basket/useBasket';
import useStore from '@/shared/stores/useStore';
import useBooleanToggle from '@/shared/useBooleanToggle';
import dictionaryService from '@/core/dictionary/dictionary.service';

enum PickAndPackStep {
    ItemSelection = 0,
    StoreSelection = 1,
    Confirmation = 2,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PickAndPack',
  props: {
    deliverable: null
  },
  setup(__props: any) {

const props = __props as {
    deliverable: BasketDeliverableForPickAndPack;
}




const labels = useBlockLabels();
const selectedItems: Ref<DeliverableSelectItem[]> = ref([]);
const selectedStore: Ref<ClickAndCollectStoreInventoryResult | null> = ref(null);
const step: Ref<PickAndPackStep> = ref(PickAndPackStep.ItemSelection);
const isItemSelectionStep: ComputedRef<boolean> = computed(() => step.value === PickAndPackStep.ItemSelection);
const isStoreSelectionStep: ComputedRef<boolean> = computed(() => step.value === PickAndPackStep.StoreSelection);
const isConfirmationStep: ComputedRef<boolean> = computed(() => step.value === PickAndPackStep.Confirmation);
const clickAndCollectStatus: Ref<(ClickAndCollectStoreInventoryResult | undefined)[] | undefined> = ref([]);

const { getSelectItemsFromItems, getClickAndCollectStatus } = useDeliverables();
const { changeToClickAndCollect, basket } = useBasket();
const { isToggled, toggle } = useBooleanToggle();

const existingClickAndCollectStoreId: ComputedRef<string | null> = computed(() => {
    const clickAndCollectDeliverable = basket.value.deliverables.find(d => d.type === CheckoutDeliverableType.ClickAndCollect) as BasketDeliverableForClickAndCollect | undefined;
    return clickAndCollectDeliverable?.storeId ?? null;
});

const existingClickAndCollectStore: ComputedRef<StoreModel | null> = computed(() => {
    return existingClickAndCollectStoreId.value ? unref<StoreModel>(useStore(existingClickAndCollectStoreId.value)) : null;
});

const items: ComputedRef<DeliverableSelectItem[]> = computed(() => {
    return getSelectItemsFromItems(props.deliverable.items as BaseProductItem[], i => i.clickAndCollectEnabled);
});

const heading: ComputedRef<string> = computed(() => {
    if (isStoreSelectionStep.value) {
        return labels.pickAndPackDeliverableChangeToClickAndCollectChooseStoreHeading;
    } else if (isConfirmationStep.value) {
        return dictionaryService.pluralize(itemsNotAvailableInSelectedStore.value.length, labels.pickAndPackDeliverableChangeToClickAndCollectItemsNotAvailableHeading, labels.pickAndPackDeliverableChangeToClickAndCollectItemsNotAvailableHeadingPlural);
    } else {
        return labels.pickAndPackDeliverableChangeToClickAndCollectSelectItemsHeading;
    };
});

const initClickAndCollect = (): void => {
    step.value = PickAndPackStep.ItemSelection;
    selectedItems.value = items.value.filter(i => i.selectable);
    toggle();
};

const initSelectStore = async(): Promise<void> => {
    selectedStore.value = null;
    clickAndCollectStatus.value = [];
    const ids = selectedItems.value.map(sel => sel.itemId);
    step.value = PickAndPackStep.StoreSelection;
    clickAndCollectStatus.value = await getClickAndCollectStatus(ids);
};

const maybeInitSelectStore = (): void => {
    if (existingClickAndCollectStoreId.value) {
        confirm();
    } else {
        initSelectStore();
    }
};

const selectStore = (store: ClickAndCollectStoreInventoryResult): void => {
    selectedStore.value = store;
    const hasAllItemsAvailable = selectedStore.value.itemIdsNotAvailable.length < 1;
    if (hasAllItemsAvailable) {
        confirm();
    } else {
        step.value = PickAndPackStep.Confirmation;
    }
};

const itemsNotAvailableInSelectedStore: ComputedRef<DeliverableSelectItem[]> = computed(() => {
    const itemsNotAvailable = items.value.filter(item => selectedStore.value?.itemIdsNotAvailable.includes(item.itemId) ?? [] as DeliverableSelectItem[]);
    return itemsNotAvailable;
});

const confirm = async(): Promise<void> => {
    let request;
    if (existingClickAndCollectStoreId.value) {
        request = {
            clickAndCollectStoreId: existingClickAndCollectStoreId.value,
            itemIds: selectedItems.value.map(i => i.itemId),
        };
    } else if (selectedStore.value) {
        request = {
            clickAndCollectStoreId: selectedStore.value.storeId,
            itemIds: selectedStore.value.itemIdsAvailable,
        };
    }
    toggle();
    await changeToClickAndCollect(request);
};


return (_ctx: any,_cache: any) => {
  const _component_c_icon = _resolveComponent("c-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(BlockLabelWithNamedArguments, {
      label: __props.deliverable.text
    }, null, 8, ["label"]),
    (__props.deliverable.changeToClickAndCollectEnabled)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("button", {
            type: "button",
            class: "c-magic-line c-magic-line--underline text-dusty-grey mt-x4",
            onClick: initClickAndCollect
          }, _toDisplayString(_unref(labels).pickAndPackDeliverableChangeToClickAndCollectLink), 1),
          _createVNode(BasketDeliverableDialog, {
            "is-open": _unref(isToggled),
            heading: _unref(heading),
            onClose: _unref(toggle)
          }, _createSlots({
            navigation: _withCtx(() => [
              _createVNode(_Transition, {
                name: "o-anim-fade-300",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (_unref(isItemSelectionStep))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        class: "c-button c-button--primary mx-auto",
                        disabled: selectedItems.value?.length < 1,
                        onClick: _withModifiers(maybeInitSelectStore, ["prevent"])
                      }, _toDisplayString(_unref(labels).pickAndPackDeliverableChangeToClickAndCollectSelectItemsButton), 9, _hoisted_6))
                    : (_unref(isStoreSelectionStep))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7))
                      : (_unref(isConfirmationStep))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "flex flex-nowrap items-center",
                              onClick: _withModifiers(initSelectStore, ["prevent"])
                            }, [
                              _createVNode(_component_c_icon, {
                                name: "icon-arrow-small",
                                class: "rotate-180 w-8 h-8 mt-1 mr-6 xl:h-10 xl:mr-8 xl:-mt-1"
                              }),
                              _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(labels).pickAndPackDeliverableChangeToClickAndCollectItemsNotAvailableButtonBack), 1)
                            ], 8, _hoisted_9),
                            _createElementVNode("button", {
                              type: "button",
                              class: "c-button c-button--primary",
                              onClick: _withModifiers(confirm, ["prevent"])
                            }, _toDisplayString(_unref(labels).pickAndPackDeliverableChangeToClickAndCollectItemsNotAvailableButton), 9, _hoisted_11)
                          ]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              _createVNode(_Transition, {
                name: "o-anim-fade-300",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (_unref(isItemSelectionStep))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createVNode(BasketDeliverableItemsSelection, {
                          selected: selectedItems.value,
                          "onUpdate:selected": _cache[0] || (_cache[0] = ($event: any) => ((selectedItems).value = $event)),
                          items: _unref(items),
                          "error-label": _unref(labels).pickAndPackDeliverableChangeToClickAndCollectSelectItemsItemNotAvailable
                        }, null, 8, ["selected", "items", "error-label"])
                      ]))
                    : (_unref(isStoreSelectionStep))
                      ? (_openBlock(), _createBlock(BasketDeliverableStoreSelection, {
                          key: 1,
                          "stores-status": clickAndCollectStatus.value,
                          "select-label": _unref(labels).pickAndPackDeliverableChangeToClickAndCollectChooseStoreButton,
                          class: "min-h-250",
                          onSelect: selectStore
                        }, null, 8, ["stores-status", "select-label"]))
                      : (_unref(isConfirmationStep))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createElementVNode("div", {
                              class: "text-center mb-20",
                              innerHTML: _ctx.$dictionary.pluralize(_unref(itemsNotAvailableInSelectedStore).length, _unref(labels).pickAndPackDeliverableChangeToClickAndCollectItemsNotAvailableDescription, _unref(labels).pickAndPackDeliverableChangeToClickAndCollectItemsNotAvailableDescriptionPlural, { store: selectedStore.value.storeTitle })
                            }, null, 8, _hoisted_5),
                            _createVNode(BasketDeliverableItemsSelection, {
                              items: _unref(itemsNotAvailableInSelectedStore),
                              "read-only": true,
                              class: "mx-auto max-w-500 min-h-150 flex flex-col justify-center"
                            }, null, 8, ["items"])
                          ]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 2
          }, [
            (_unref(isItemSelectionStep) && _unref(existingClickAndCollectStoreId))
              ? {
                  name: "heading",
                  fn: _withCtx(() => [
                    _createElementVNode("h2", null, _toDisplayString(_unref(heading)), 1),
                    _createElementVNode("div", _hoisted_1, [
                      _createElementVNode("span", null, _toDisplayString(_unref(labels).pickAndPackDeliverableChangeToClickAndCollectSelectItemsExistingStoreLabel), 1),
                      _createElementVNode("strong", _hoisted_2, _toDisplayString(_unref(existingClickAndCollectStore)?.name), 1)
                    ])
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["is-open", "heading", "onClose"])
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})